import { CheckCircleFilled, CloseCircleFilled, MinusCircleFilled } from "@ant-design/icons";

import { ERROR_500, GRAY_500, PRIMARY_600, SUCCESS_500, WARNING_500 } from "../../../constant/colors";
import {
  AGENTIC_RUN_STATUS,
  AUDIT_STEP_RESULT,
  AGENTIC_STEP_NAMES,
  EE_SUBMISSION_RESULT,
  EMR_GATHERING_OUTCOME,
  HITL_TASK_TYPES,
  NAVIGATOR_PAUSE_TASK_TYPE_BY_STEP,
  NON_AGENT_STEP_NAMES,
  RUN_FINAL_OUTCOME
} from "../../../constant/agenticPlatform";

/**
 * Icon per audit-step result (`passed` / `failed` / `skipped`). Single source of
 * truth for WHICH glyph represents each result across the agentic UI — the run
 * modal's step chips (`STEP_RESULT_VISUAL`) and the HITL summary dots
 * (`SUMMARY_RESULT_VISUAL`) build their own colors/shape around these but must
 * agree on the icon, so a result never shows two different glyphs in two places.
 */
export const RESULT_ICON = Object.freeze({
  passed: CheckCircleFilled,
  failed: CloseCircleFilled,
  skipped: MinusCircleFilled
});

/**
 * Single source of truth for run-status visuals — shared by the patient-page
 * `<AgenticRunsButton>` (label text color) and the `<AgenticRunsModal>`
 * (`<Tag>` color). Keys mirror the platform's `AgenticRunStatus` enum
 * (`agentic_workflow_runs.status`) so a new bucket added on the platform is
 * surfaced consistently across both surfaces by editing this one map.
 *
 * Each entry carries:
 *   - `labelKey` / `fallback`  — i18n label (button + modal use the SAME copy)
 *   - `antdTagColor`           — Antd `<Tag>` color preset for the modal
 *   - `hex`                    — explicit hex for the button's label text
 */
export const RUN_STATUS_META = Object.freeze({
  running: {
    labelKey: "agentic_runs.status.running",
    fallback: "Agent running",
    antdTagColor: "processing",
    hex: PRIMARY_600
  },
  // Live, but nothing to act on: the tree is being reset onto the current
  // build. `loading` drives the spinner — the one status where the UI has to say
  // "wait", not "do something".
  restarting: {
    labelKey: "agentic_runs.status.restarting",
    fallback: "Restarting…",
    antdTagColor: "processing",
    hex: PRIMARY_600,
    loading: true
  },
  waiting_hitl: {
    labelKey: "agentic_runs.status.waiting_hitl",
    fallback: "Waiting for navigator",
    // In-flight / action-needed: the workflow is alive and parked on a
    // human-in-the-loop task, not a quiet terminal state — so it uses the
    // warning palette to read as "waiting on a person".
    antdTagColor: "warning",
    hex: WARNING_500
  },
  // The consent link WAS sent — and the wait window then closed without a
  // signature, so the patient went back to the queue (see `HANDBACK_CAUSE`:
  // "the patient didn't sign the consent in time"). Named for the ENDING, not
  // the action: "Consent sent" in success-green read as an accomplishment when
  // it is actually a give-up, which is the opposite of what a navigator
  // scanning the list needs to see.
  not_signed_in_time: {
    labelKey: "agentic_runs.status.not_signed_in_time",
    fallback: "Not signed in time",
    antdTagColor: "warning",
    hex: WARNING_500
  },
  // Waiting on the PATIENT to sign — not on a navigator. Distinct from
  // `waiting_hitl` (which needs YOU) and from `running` (where the agent is
  // actually working): nothing moves here until the patient clicks the link.
  awaiting_signature: {
    labelKey: "agentic_runs.status.awaiting_signature",
    fallback: "Waiting for patient",
    antdTagColor: "processing",
    hex: PRIMARY_600
  },
  // Waiting on a person to upload EMR screenshots. Kept as its own bucket
  // rather than folded into `waiting_hitl`: the detail differs, and the pause
  // is resumed by an upload, not by answering a task. Distinct from the VLM
  // parse that follows the upload, which IS the agent working.
  awaiting_screenshots: {
    labelKey: "agentic_runs.status.awaiting_screenshots",
    fallback: "Waiting for navigator",
    antdTagColor: "processing",
    hex: PRIMARY_600
  },
  // A NAVIGATOR declined the task (chose "Don't send" on the consent decision).
  // Named for who decided, because the alternative reading — the PATIENT
  // declining consent — is a completely different outcome, and a bare
  // "Declined" doesn't say which happened.
  declined: {
    labelKey: "agentic_runs.status.declined",
    fallback: "FN declined task",
    // A navigator declining the consent send is a legitimate decision, not a
    // failure — so it reads as a neutral/gray terminal state (like
    // not_eligible / oop_met), NOT the red error palette reserved for genuine
    // crashes (`failed`).
    antdTagColor: "default",
    hex: GRAY_500
  },
  not_eligible: {
    labelKey: "agentic_runs.status.not_eligible",
    fallback: "Not eligible",
    antdTagColor: "default",
    hex: GRAY_500
  },
  oop_met: {
    labelKey: "agentic_runs.status.oop_met",
    fallback: "OOP met",
    antdTagColor: "default",
    hex: GRAY_500
  },
  no_active_medications: {
    labelKey: "agentic_runs.status.no_active_medications",
    fallback: "No active meds",
    antdTagColor: "default",
    hex: GRAY_500
  },
  // Consent IS in hand, but the enrollment side left work behind — nothing was
  // enrollable, or the enrollments that ran didn't submit — so the case went
  // back to a navigator. Warning, not neutral: the run is over, but somebody
  // still has to pick the patient up.
  handed_back: {
    labelKey: "agentic_runs.status.handed_back",
    fallback: "Handed back",
    antdTagColor: "warning",
    hex: WARNING_500
  },
  enrolled: {
    labelKey: "agentic_runs.status.enrolled",
    fallback: "Enrolled",
    antdTagColor: "success",
    hex: SUCCESS_500
  },
  // The agent enrolled SOME of the programs that could help this patient — the
  // rest went back to the queue (not configured for auto-enrollment, or the
  // submission failed). Still GREEN: an enrollment happened, and the count says
  // the scope rather than framing a success as a shortfall. Shown only when the
  // numbers differ; enrolling everything eligible reads as a plain "Enrolled".
  enrolled_some: {
    labelKey: "agentic_runs.status.enrolled_some",
    fallback: "Enrolled {{enrolled}} of {{eligible}}",
    antdTagColor: "success",
    hex: SUCCESS_500
  },
  // A HITL pause ran out of time — nobody acted within the window, so the run
  // handed the patient back and stopped. Warning, not error: the agent did its
  // job right up to needing a person, and like `consent_obtained_handoff` the
  // run is over but somebody still has to pick the patient up. Named for the
  // ENDING and for WHO was missing — a bare "Expired" reads as though the
  // patient's eligibility lapsed, which is a different thing entirely.
  hitl_timeout: {
    labelKey: "agentic_runs.status.hitl_timeout",
    fallback: "No response in time",
    antdTagColor: "warning",
    hex: WARNING_500
  },
  // The catch-all for a run that FINISHED on a verdict this map doesn't name.
  // Exists so an unmapped outcome degrades to an honest "it's over" instead of
  // falling through to the caller's "RUNNING" default — a finished run must
  // never claim to be in flight.
  completed: {
    labelKey: "agentic_runs.status.completed",
    fallback: "Completed",
    antdTagColor: "default",
    hex: GRAY_500
  },
  failed: {
    labelKey: "agentic_runs.status.failed",
    fallback: "Failed",
    antdTagColor: "error",
    hex: ERROR_500
  }
});

/**
 * Friendly labels for the supported hypercare reasons (HypercareReason on the
 * platform). Single source of truth — shared by the run modal and the HITL task
 * modal, so adding a reason on the platform is surfaced everywhere by editing
 * this one map. Unknown codes fall through to the raw code via
 * {@link hypercareReasonLabel}.
 */
export const HYPERCARE_REASON_LABELS = Object.freeze({
  COMMERCIAL_NEW_MEDICATION: "Commercial · new medication",
  COMMERCIAL_DONE_ESTABLISHED: "Commercial · done / established",
  COMMERCIAL_ACTIVE_TREATMENT: "Commercial · active treatment",
  MEDICARE_NEW_MEDICATION: "Medicare · new medication",
  MEDICARE_DONE_ESTABLISHED: "Medicare · done / established"
});

/** Label for a hypercare reason code; unknown codes return the raw code. */
export const hypercareReasonLabel = (code) => HYPERCARE_REASON_LABELS[code] || code;

/**
 * The display bucket for a TERMINAL run, derived from `run.finalOutcome` (the
 * verdict) — `run.status` is pure lifecycle (succeeded/failed/expired) and never
 * carries it. Verdicts with no bucket fall back to the generic label
 * (failure keeps the failed bucket via `getRunStatusMeta`).
 */
const FINAL_OUTCOME_META_KEY = Object.freeze({
  [RUN_FINAL_OUTCOME.CONSENT_SENT_BY_NAVIGATOR]: "not_signed_in_time",
  [RUN_FINAL_OUTCOME.FN_CONSENT_SENDING_DECLINED]: "declined",
  [RUN_FINAL_OUTCOME.NOT_ELIGIBLE]: "not_eligible",
  [RUN_FINAL_OUTCOME.OOP_MET]: "oop_met",
  [RUN_FINAL_OUTCOME.NO_ACTIVE_MEDICATIONS]: "no_active_medications",
  // All three are the same pill — the run handed the patient back. The WHY
  // differs (and the modal spells it out); the pill only says what happened.
  // `consent_obtained_handoff` is the pre-MVP-31690 value, kept for old runs.
  [RUN_FINAL_OUTCOME.CONSENT_OBTAINED_HANDOFF]: "handed_back",
  [RUN_FINAL_OUTCOME.NOTHING_ENROLLABLE]: "handed_back",
  [RUN_FINAL_OUTCOME.ENROLLMENT_NONE_SUBMITTED]: "handed_back",
  [RUN_FINAL_OUTCOME.HITL_TIMEOUT]: "hitl_timeout",
  [RUN_FINAL_OUTCOME.FAILURE]: "failed"
});

/**
 * Every navigator pause currently holding the run TREE — the steps named by
 * {@link NAVIGATOR_PAUSE_TASK_TYPE_BY_STEP} that are still open
 * (`result == null`). Each carries the `agenticRunId` of the row that owns it,
 * which is what a Tasks-hub deep link has to address. Preserves the order the
 * payload arrived in (newest step first) — no caller depends on it; ask by
 * identity, not by position.
 *
 * Reads the STEPS rather than run rows because that's all there is to read: the
 * latest-run payload is the root row plus every run's steps merged, so a child's
 * `status`/`hitlTaskType` never crosses the wire. A pause is written and closed
 * in the same breath as that flag (`pauseForHitl` on the platform), so an open
 * pause step is an exact stand-in for `waiting_hitl` on the row it belongs to.
 *
 * Usually 0 or 1. Several at once is a genuine state, not a glitch: the
 * conductor fans out one express-enrollment child per enrollable pap, and each
 * parks on its own `ee_review`.
 */
/**
 * How many programs the agent enrolled, out of the programs that could have
 * helped this patient.
 *
 * The DENOMINATOR is deliberately the ELIGIBLE set, not the recommended set and
 * not the agent-capable set:
 *   - recommended would include paps the patient isn't even on (they failed the
 *     financial-assistance check) — not work for anyone, so counting them would
 *     understate the agent for no reason;
 *   - agent-capable would hide eligible programs a navigator still has to
 *     enroll by hand, which is exactly the fact the count exists to carry.
 *
 * Both numbers come from steps the payload already merges across the run tree,
 * so this needs nothing new from the platform. `eligibleCount` is the count of
 * paps the eligibility step passed through to enrollment detection.
 */
export const getEnrollmentCounts = (run) => {
  const steps = run?.steps ?? [];
  const candidates = steps.find((step) => step.stepName === AGENTIC_STEP_NAMES.ENROLLMENT_CANDIDATES);
  const enrolled = steps.filter(
    (step) => step.stepName === AGENTIC_STEP_NAMES.EE_SUBMIT && step.outcome === EE_SUBMISSION_RESULT.SUBMITTED
  ).length;
  return { enrolled, eligible: candidates?.payload?.eligibleCount ?? 0 };
};

/**
 * The WHAT half of a wait label — the status names WHO is holding the journey,
 * this names what they owe. Carries a `fallback` like every status bucket
 * above, so a missing translation degrades to English rather than to a raw key.
 */
const WAIT_DETAIL = Object.freeze({
  screenshots: { labelKey: "agentic_runs.wait_detail.screenshots", fallback: "EMR screenshots" },
  signature: { labelKey: "agentic_runs.wait_detail.signature", fallback: "consent signature" }
});

/** The same, for the three pauses a navigator can act on. */
const NAVIGATOR_WAIT_DETAIL = Object.freeze({
  [HITL_TASK_TYPES.CONSENT_DECIDE]: {
    labelKey: "agentic_runs.wait_detail.consent_decide",
    fallback: "consent decision"
  },
  [HITL_TASK_TYPES.EMR_FILL]: { labelKey: "agentic_runs.wait_detail.emr_fill", fallback: "manual EMR fill" },
  [HITL_TASK_TYPES.EE_REVIEW]: { labelKey: "agentic_runs.wait_detail.ee_review", fallback: "enrollment review" }
});

/**
 * The label for a status meta, resolving `{{enrolled}} of {{eligible}}` when the
 * bucket carries counts. Callers use this instead of `t(meta.labelKey, …)`
 * directly so the interpolated buckets can't be rendered raw.
 */
export const runStatusLabel = (meta, t, { detail = true } = {}) => {
  if (!meta) return null;
  const label = t(meta.labelKey, { defaultValue: meta.fallback, ...(meta.labelParams ?? {}) });
  // Every wait reads "who · what" — one grammar across all three of them, so a
  // reader never has to work out whether a label names a person or an artifact.
  //
  // `detail: false` is for surfaces with no room for the second half: the
  // patient-page chip is pinned to 150px to match its sibling action buttons,
  // where "who · what" is clipped mid-word. Those callers show the who and put
  // the full string in a tooltip, so nothing is lost — just relocated.
  if (!detail || !meta.detail) return label;
  return `${label} · ${t(meta.detail.labelKey, { defaultValue: meta.detail.fallback })}`;
};

/**
 * Is the run parked on the post-send consent wait? The platform writes the
 * `consent_signature_wait` step with `result: null` when the wait opens and
 * closes it on signature or expiry, so an open one means "waiting on the
 * PATIENT to sign the link".
 *
 * Deliberately NOT part of {@link NAVIGATOR_PAUSE_TASK_TYPE_BY_STEP}: that map
 * is pauses a NAVIGATOR can act on, and this one nobody here can — which is
 * exactly why it needs its own badge instead of reading as "Agent running".
 */
/**
 * The CURRENT event for each step name, newest first.
 *
 * `agentic_workflow_run_steps` is an append-only log, so a step that parked and
 * later resolved arrives as TWO rows — a `result: null` "waiting" row and the
 * row that resolved it. Anything asking "what is this step's state?" must read
 * the newest of them: scanning every row would keep matching the stale waiting
 * row, so a pause that a navigator answered would still read as open and the run
 * would show "Waiting for navigator" forever.
 *
 * Keyed by run AND step name, never step name alone: `run.steps` is the root
 * run's events merged with every child's, and an express-enrollment fan-out parks
 * one child PER PAP — so several concurrent `ee_human_review` events share a step
 * name and differ only by `agenticRunId`. Collapsing on the name would drop all
 * but one, hiding a pending enrollment from the navigator entirely.
 *
 * Newest by `id`, not `createdAt` — the platform writes back-to-back events
 * inside one second, and `id` is a single auto-increment sequence.
 */
export const latestStepEvents = (steps) => {
  const newestFirst = [...(steps ?? [])].sort((a, b) => b.id - a.id);
  const seen = new Set();
  return newestFirst.filter((step) => {
    const key = `${step.agenticRunId}:${step.stepName}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/** Both take CURRENT events, so a caller needing several can reduce once. */
const isOpenSignatureWait = (events) =>
  events.some((step) => step.stepName === AGENTIC_STEP_NAMES.CONSENT_SIGNATURE_WAIT && step.result == null);

/**
 * The still-open consent-decide review STEP (`result == null`). Since MVP-31712
 * a paused run writes NO standalone `consent_state_check` row — this row is the
 * pre-decision consent-state carrier instead (state on `outcome`, contact
 * context on `payload`). Exported so every consumer of "which step carries the
 * consent state" shares one rule (AgenticRunsModal's fold/banner and
 * HitlTaskModal's steps summary).
 */
export const isOpenConsentReview = (step) =>
  step.stepName === AGENTIC_STEP_NAMES.CONSENT_HUMAN_REVIEW && step.result == null;

/**
 * The row to read that state FROM, given CURRENT events. The standalone check
 * row wins when present — an older run has both, and only the check row
 * carries the context payload there. Otherwise the pending pause row is it.
 * The one composed rule shared by AgenticRunsModal's fold/banner and
 * HitlTaskModal's steps summary.
 */
export const findConsentStateCarrier = (steps) =>
  steps.find((s) => s.stepName === AGENTIC_STEP_NAMES.CONSENT_STATE_CHECK) ?? steps.find(isOpenConsentReview);

/**
 * Is the run parked on the OPERATOR uploading EMR screenshots? Takes CURRENT
 * events, so the upload's `processing_screenshots` event closes it — that is
 * what separates "we need screenshots" from "the VLM is reading them".
 *
 * Absent from {@link NAVIGATOR_PAUSE_TASK_TYPE_BY_STEP} for the same reason as
 * the signature wait: no navigator can resume it.
 */
export const isOpenScreenshotWait = (events) =>
  events.some(
    (step) =>
      step.stepName === AGENTIC_STEP_NAMES.EMR_GATHERING &&
      step.outcome === EMR_GATHERING_OUTCOME.WAITING_FOR_SCREENSHOTS &&
      step.result == null
  );

const openNavigatorPauses = (events) =>
  events.filter((step) => NAVIGATOR_PAUSE_TASK_TYPE_BY_STEP[step.stepName] && step.result == null);

/**
 * The steps worth showing a navigator: current state per step, minus the ones a
 * RESTART abandoned.
 *
 * A superseded step is a wait the restarted run never came back to — it records
 * something about the release, not about the patient, same as `run_resumed`.
 * Only ever written over a step whose newest event was still awaiting (the
 * platform's sweep skips resolved ones), so dropping it can never hide work that
 * actually happened.
 *
 * Filtered AFTER `latestStepEvents`, never before: the superseded event is the
 * step's closing one, so dropping it from the raw log would leave the earlier
 * awaiting event newest and the step would read "In progress" forever — the
 * exact thing the sweep exists to prevent.
 */
export const currentAgentSteps = (steps) =>
  latestStepEvents(steps).filter((step) => step?.result !== AUDIT_STEP_RESULT.SUPERSEDED);

export const findOpenNavigatorPauses = (run) => openNavigatorPauses(latestStepEvents(run?.steps));

/**
 * The step log as the PATIENT's journey: the agent's own work, with run-level
 * bookkeeping dropped. `run_resumed` records that a release forced a replay —
 * true of the run, not something the agent did for this patient.
 *
 * Used by every surface that RENDERS the log or dates it: the patient-page
 * modal, the button's "last activity" stamp, and the Tasks-hub HITL modal.
 * Filtering in only one of them let a restart masquerade as activity on the
 * others — the bug this exists to prevent.
 *
 * `getRunStatusMeta` deliberately reads the raw log instead: it matches on open
 * pauses and open waits, and neither a `run_resumed` (not a pause step name) nor
 * a superseded step (`result` is set, not null) can satisfy those predicates.
 */
export const agentStepEvents = (run) =>
  (run?.steps ?? []).filter((step) => !NON_AGENT_STEP_NAMES.includes(step?.stepName));

/**
 * The journey's restarts, oldest first — the boundaries the log is split on.
 *
 * ONE per restart, though every re-entered run in the tree writes its own
 * `run_resumed` within the same second: only the ROOT's marks the journey
 * restarting, the children's are per-row bookkeeping. `run.steps` is the root's
 * events merged with every child's, so the root's are the ones whose
 * `agenticRunId` is the run's own id.
 *
 * Versions come off the structured payload, never by parsing the `outcome`
 * string — that arrow form exists to be read by a human, not by this.
 */
export const restartEvents = (run) =>
  (run?.steps ?? [])
    .filter((step) => step?.stepName === AGENTIC_STEP_NAMES.RUN_RESUMED && step?.agenticRunId === run?.id)
    .sort((a, b) => a.id - b.id)
    .map((step) => ({
      id: step.id,
      createdAt: step.createdAt,
      fromVersion: step.payload?.before?.codeVersion ?? null,
      toVersion: step.payload?.after?.codeVersion ?? null
    }));

/**
 * Which attempt a step belongs to — 0 before the first restart, 1 after it, and
 * so on. Steps are compared by `id`, a single auto-increment sequence, so this
 * needs no timestamps.
 *
 * Exists because a restart re-runs the whole journey: the same step name appears
 * once per attempt, and anything pairing events across that boundary is pairing
 * two different waits. See `waitByStepName` in the modal, which reported a
 * 19-minute pause as 46 minutes by taking one attempt's opener and the next
 * attempt's answer.
 */
export const attemptOf = (stepId, restarts) => (restarts ?? []).filter((restart) => restart.id < stepId).length;

/**
 * When the journey last DID something — the newest step across the run TREE.
 *
 * The run row's own `createdAt` answers a different, much weaker question. These
 * are durable workflows: an rx-conductor run lives up to 28 days and can sit
 * parked on a navigator for a week, so its start time says nothing about whether
 * anything has happened lately. `auditStep` inserts without touching the parent
 * row, so the row's clock doesn't move when a child does the work either — which
 * is why the activity feed orders on MAX(step.createdAt) rather than the row.
 * This is the same value, derived client-side from the steps already in the
 * latest-run payload, so both surfaces agree by construction.
 *
 * NOT a heartbeat: a step is a phase boundary. A journey four days into a
 * consent-signature wait has written two steps (one opening the window, one on
 * wake), so "4 days ago" is truthful but does not mean stuck — liveness lives in
 * Temporal's workflow history, not in these tables.
 *
 * Returns null for a run that has written no step yet.
 */
export const getLastActivityAt = (run) => {
  let newest = null;
  for (const step of agentStepEvents(run)) {
    if (!step?.createdAt) continue;
    if (newest === null || new Date(step.createdAt) > new Date(newest)) newest = step.createdAt;
  }
  return newest;
};

/**
 * Meta lookup for a RUN (not a bare status string). `run.status` is pure
 * lifecycle: alive runs resolve directly; terminal runs resolve through
 * `finalOutcome` via `FINAL_OUTCOME_META_KEY`. A failed run with an unmapped
 * verdict still reads "Failed"; a succeeded run with an unmapped code falls
 * back to the generic label.
 *
 * The run passed here is the JOURNEY ROOT (`rx-conductor`), and its own status
 * is deliberately COARSE: `waiting_hitl` on the root means `consent_decide`, the
 * one pause the conductor itself owns. Pauses owned by CHILD workflows live on
 * their own rows, which aren't in the payload — so the root alone reads
 * `running` while a child holds a human.
 *
 * That was survivable while every child pause was operator-facing
 * (`emr_screenshots`, which has its own surface and no navigator task). It stops
 * being survivable with `ee_review`: it is a NAVIGATOR pause on a child, so
 * trusting the root would tell a navigator the agent is busy at the exact moment
 * it is waiting on them. Hence the tree read below — any open navigator pause
 * anywhere in the tree makes the whole journey read as awaiting a navigator.
 *
 * WHICH pause is still not a run-level answer (a fan-out can hold several at
 * once); that stays per-step, in the modal's timeline and the Tasks hub.
 */
export const getRunStatusMeta = (run) => {
  if (!run?.status) {
    return null;
  }
  // BEFORE the pause lookup below. `markTreeRestarting` moves the row's status
  // but leaves its awaiting step open, so the pause branch would still match and
  // render "Waiting for navigator" — offering a task the Tasks hub has just
  // hidden and that `getWorkflowIdForRun` would refuse to resume.
  if (run.status === AGENTIC_RUN_STATUS.RESTARTING) {
    return RUN_STATUS_META.restarting;
  }
  if (
    run.status === AGENTIC_RUN_STATUS.SUCCEEDED ||
    run.status === AGENTIC_RUN_STATUS.FAILED ||
    run.status === AGENTIC_RUN_STATUS.EXPIRED
  ) {
    // Both enrollment verdicts resolve through the COUNTS rather than the
    // verdict alone, so neither gets a `FINAL_OUTCOME_META_KEY` bucket:
    // `ENROLLED` only means "everything the agent could attempt succeeded",
    // which is still a partial job whenever an eligible program wasn't
    // agent-enrollable. The count is what tells those apart.
    if (run.finalOutcome === RUN_FINAL_OUTCOME.ENROLLED || run.finalOutcome === RUN_FINAL_OUTCOME.ENROLLMENT_PARTIAL) {
      const { enrolled, eligible } = getEnrollmentCounts(run);
      if (enrolled > 0 && eligible > enrolled) {
        return { ...RUN_STATUS_META.enrolled_some, labelParams: { enrolled, eligible } };
      }
      return RUN_STATUS_META.enrolled;
    }
    const bucket = FINAL_OUTCOME_META_KEY[run.finalOutcome];
    if (bucket && RUN_STATUS_META[bucket]) {
      return RUN_STATUS_META[bucket];
    }
    // An unmapped verdict still resolves to a TERMINAL bucket. Returning null
    // here handed the caller a "no status" it renders as "RUNNING", so a
    // finished run read as still in flight — which is exactly what
    // `consent_obtained_handoff` did before it was mapped above. Every outcome
    // the platform writes is mapped today, so this is a guard against the NEXT
    // one being added without a bucket, not a live path.
    //
    // Each lifecycle gets its own fallback rather than sharing `completed`: an
    // expired run reading "Completed" would claim success for a task nobody
    // answered, the same class of lie the null-return used to tell.
    if (run.status === AGENTIC_RUN_STATUS.FAILED) {
      return RUN_STATUS_META.failed;
    }
    if (run.status === AGENTIC_RUN_STATUS.EXPIRED) {
      return RUN_STATUS_META.hitl_timeout;
    }
    return RUN_STATUS_META.completed;
  }
  // Checked only for a LIVE run (the terminal branch above has already
  // returned): an open pause step left behind on a finished run is stale
  // bookkeeping, and the run's verdict outranks it.
  const currentEvents = latestStepEvents(run?.steps);
  const navigatorPauses = openNavigatorPauses(currentEvents);
  if (navigatorPauses.length > 0) {
    // One open pause is enough to name the task: a bare "Waiting for navigator"
    // tells a reader less than the row already knew. An express-enrollment
    // fan-out parks one review PER PAP, so several can be open at once — the
    // NEWEST wins, which is both deterministic (`latestStepEvents` is ordered
    // newest-first) and the pause the row's own timestamp already refers to.
    const [newestPause] = navigatorPauses;
    return {
      ...RUN_STATUS_META.waiting_hitl,
      detail: NAVIGATOR_WAIT_DETAIL[NAVIGATOR_PAUSE_TASK_TYPE_BY_STEP[newestPause.stepName]] ?? null
    };
  }
  // Checked AFTER the navigator pause: if both are somehow open, work the
  // navigator owns outranks a wait nobody here can act on.
  if (isOpenSignatureWait(currentEvents)) {
    return { ...RUN_STATUS_META.awaiting_signature, detail: WAIT_DETAIL.signature };
  }
  // Ordered last only for consistency: gathering finishes long before consent,
  // so this can't co-occur with the waits above.
  if (isOpenScreenshotWait(currentEvents)) {
    return { ...RUN_STATUS_META.awaiting_screenshots, detail: WAIT_DETAIL.screenshots };
  }
  return RUN_STATUS_META[run.status] ?? null;
};
